import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  Menu, X, ChevronDown, LogOut, User as UserIcon, ArrowRight,
} from "lucide-react";
import { LOGO_STACKED } from "../lib/api";
import { useAuth } from "../lib/auth";

// Mega menu structure: clean typography only, no icons
// Optional `feature` block becomes the highlighted right-side card
const NAV = [
  { label: "Home", to: "/" },
  {
    label: "About",
    to: "/about",
    mega: true,
    columns: [
      {
        heading: "Discover SMZBT",
        items: [
          { label: "Our Story",      to: "/about",   desc: "A family. A movement. A home in Hampton." },
          { label: "Church History", to: "/history", desc: "Generations of faith, anchored in this city." },
          { label: "Senior Pastor",  to: "/pastor",  desc: "Rev. Jerome A. Barber — a life of service." },
        ],
      },
      {
        heading: "Get Involved",
        items: [
          { label: "Ministries",  to: "/ministries",  desc: "Choirs, youth, missions, men, women, outreach." },
          { label: "New Members", to: "/members/new", desc: "Welcome home. Take your first step with us." },
        ],
      },
    ],
    feature: {
      image: "/images/branding/congregation-family.jpg",
      overline: "Plan a Visit",
      title: "There's a seat reserved for you.",
      body: "Sundays at 7:45 AM & 10:00 AM",
      cta: "Plan Your Visit",
      to: "/about",
    },
  },
  {
    label: "Connect",
    to: "/calendar",
    mega: true,
    columns: [
      {
        heading: "What's Happening",
        items: [
          { label: "Church Calendar", to: "/calendar",    desc: "Services, Bible study, youth, special events." },
          { label: "Book the Pastor", to: "/book-pastor", desc: "Schedule a visit, sermon, panel, or interview." },
          { label: "Prayer Request",  to: "/prayer",      desc: "September is Prayer Month — agree with us in prayer." },
          { label: "Family Life Conference", to: "/family-life-conference", desc: "October — revival, prayer service & family reunion." },
        ],
      },
      {
        heading: "Grow With Us",
        items: [
          { label: "Music",    to: "/music",    desc: "Choirs, worship arts, and instrumental ensembles." },
          { label: "E-Campus", to: "/e-campus", desc: "Learn online. Discipleship at your own pace." },
        ],
      },
    ],
    feature: {
      image: "/images/branding/pastor-worship.jpg",
      overline: "Live Worship",
      title: "Worship with us, anywhere.",
      body: "Stream Sunday service in HD — live or on demand.",
      cta: "Watch Live",
      to: "/live-stream",
    },
  },
  {
    label: "Media",
    to: "/media-library",
    children: [
      { label: "Live Stream",              to: "/live-stream" },
      { label: "Media Library",            to: "/media-library" },
      { label: "Ministry Media Requests",  to: "/ministry-media" },
    ],
  },
];

export default function Navbar() {
  const [mobile, setMobile] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const { user, logout } = useAuth() || {};
  const nav = useNavigate();

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass" data-testid="site-header">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3" data-testid="logo-home-link">
          <img src={LOGO_STACKED} alt="Sixth Mount Zion" className="h-12 w-auto" />
          <div className="hidden md:block">
            <div className="font-sans font-black text-lg leading-none tracking-tight">SIXTH MOUNT ZION</div>
            <div className="text-[10px] tracking-[0.25em] text-white/60 uppercase mt-1 font-medium">Baptist Temple · Hampton, VA</div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1" data-testid="primary-nav">
          {NAV.map((item) => {
            const isOpen = openMenu === item.label;
            return (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setOpenMenu(item.label)}
                onMouseLeave={() => setOpenMenu(null)}
              >
                <NavLink
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `px-4 py-2 text-sm tracking-wide uppercase font-medium transition-colors flex items-center gap-1 ${
                      isActive ? "text-ember" : "text-white/85 hover:text-white"
                    }`
                  }
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  {item.label}
                  {(item.children || item.mega) && (
                    <ChevronDown className={`h-3 w-3 opacity-60 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  )}
                </NavLink>

                {/* MEGA MENU */}
                {item.mega && isOpen && (
                  <MegaPanel item={item} />
                )}

                {/* Standard dropdown */}
                {item.children && isOpen && (
                  <div className="absolute top-full left-0 mt-0 w-56 glass border border-white/10 py-2">
                    {item.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        className="block px-4 py-2 text-sm text-white/80 hover:text-ember hover:bg-white/5 transition-colors font-medium"
                        data-testid={`subnav-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Right */}
        <div className="flex items-center gap-3">
          <Link
            to="/donate"
            className="hidden md:inline-flex glass-light px-5 py-2 rounded-full text-sm font-semibold tracking-wider uppercase text-white hover:bg-white/10 transition-all"
            data-testid="header-giving-button"
          >
            Giving
          </Link>
          {user ? (
            <div className="hidden md:flex items-center gap-3 text-sm">
              <Link to="/members" className="text-xs uppercase tracking-widest text-white/60 hover:text-ember font-bold" data-testid="header-leadership-portal-link">
                Leadership Portal
              </Link>
              <span className="text-white/30">·</span>
              <span className="text-white/70">Hi, {user.name?.split(" ")[0]}</span>
              <button
                onClick={() => { logout(); nav("/"); }}
                className="text-white/60 hover:text-white p-1.5"
                data-testid="header-logout-btn"
                title="Logout"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-3 text-sm">
              <Link to="/members" className="text-xs uppercase tracking-widest text-white/55 hover:text-ember font-bold" data-testid="header-leadership-portal-link">
                Leadership
              </Link>
              <span className="text-white/30">·</span>
              <Link to="/login" className="inline-flex items-center text-sm text-white/70 hover:text-white" data-testid="header-login-link">
                <UserIcon className="h-4 w-4 mr-1" /> Login
              </Link>
            </div>
          )}
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setMobile(!mobile)}
            data-testid="mobile-menu-toggle"
          >
            {mobile ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobile && (
        <div className="lg:hidden glass border-t border-white/10 max-h-[80vh] overflow-y-auto">
          <div className="px-6 py-4 space-y-1">
            {NAV.map((item) => {
              const mobileChildren = item.children
                ? item.children
                : item.mega
                ? item.columns.flatMap((c) => c.items)
                : null;
              return (
                <div key={item.label}>
                  <Link
                    to={item.to}
                    onClick={() => setMobile(false)}
                    className="block py-2 text-base uppercase tracking-wider font-medium text-white"
                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  >
                    {item.label}
                  </Link>
                  {mobileChildren && (
                    <div className="pl-4 border-l border-wine-700 my-1">
                      {mobileChildren.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          onClick={() => setMobile(false)}
                          className="block py-1.5 text-sm text-white/70"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <div className="pt-3 border-t border-white/10 flex flex-col gap-2">
              <Link to="/donate" onClick={() => setMobile(false)} className="btn-wine px-4 py-2.5 text-center rounded-full text-sm uppercase tracking-wider font-semibold">Give</Link>
              <Link to="/members" onClick={() => setMobile(false)} className="btn-ghost px-4 py-2.5 text-center rounded-full text-sm uppercase tracking-wider">Leadership Portal</Link>
              {!user && <Link to="/login" onClick={() => setMobile(false)} className="btn-ghost px-4 py-2.5 text-center rounded-full text-sm uppercase tracking-wider">Member Login</Link>}
              {user && <button onClick={() => { logout(); setMobile(false); nav("/"); }} className="btn-ghost px-4 py-2.5 text-center rounded-full text-sm uppercase tracking-wider">Logout</button>}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

/* ---------- Mega Menu Panel ---------- */
function MegaPanel({ item }) {
  return (
    <div
      className="absolute top-full left-0 pt-3"
      data-testid={`mega-${item.label.toLowerCase()}`}
    >
      <div className="glass border border-white/10 shadow-2xl shadow-black/40 w-[760px] grid grid-cols-12 overflow-hidden animate-mega-in">
        {/* Columns */}
        <div className="col-span-8 p-8 grid grid-cols-2 gap-x-10 gap-y-7 bg-wine-950/40">
          {item.columns.map((col) => (
            <div key={col.heading}>
              <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-bold mb-4 pb-3 border-b border-white/10">
                {col.heading}
              </div>
              <ul className="space-y-1">
                {col.items.map((sub) => (
                  <li key={sub.to}>
                    <Link
                      to={sub.to}
                      className="group block px-3 py-2.5 -mx-3 rounded-md hover:bg-white/[0.06] transition-colors"
                      data-testid={`mega-${item.label.toLowerCase()}-${sub.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <span className="block text-[15px] font-bold text-white group-hover:text-ember transition-colors tracking-tight">
                        {sub.label}
                      </span>
                      <span className="block text-xs text-white/55 leading-snug mt-1">
                        {sub.desc}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Feature */}
        {item.feature && (
          <Link
            to={item.feature.to}
            className="col-span-4 relative group overflow-hidden"
            data-testid={`mega-${item.label.toLowerCase()}-feature`}
          >
            <img
              src={item.feature.image}
              alt=""
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-wine-950/40 via-wine-950/70 to-wine-950/95" />
            <div className="relative h-full p-7 flex flex-col justify-end">
              <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-bold mb-2">
                {item.feature.overline}
              </div>
              <h4 className="text-xl font-black leading-tight mb-1 text-white">
                {item.feature.title}
              </h4>
              <p className="text-xs text-white/70 mb-4">{item.feature.body}</p>
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ember group-hover:text-gold font-bold">
                {item.feature.cta} <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
              </span>
            </div>
          </Link>
        )}
      </div>
    </div>
  );
}
